import { Dispatch } from 'react-redux';
import { AuthActionCreators, IApplicationState } from '../';
import { ActionTypes } from './action-types';
import { IBillingDetail, IBaseResponse, ICenterBase, IApSbsItem, IBilledCenterForList, ISearchForList, IDonorEvalRecord, ICenterBillingRecord } from '@nkr/models/index';
import { DonorEvalQuery, OrganType } from '@nkr/enums/index';
import { IRecipientCenterInvoiceRequest } from '@nkr/models/recipient-center-invoice-request';
import axios, { AxiosError } from 'axios';
import { IGet } from '../authorization';

export interface INotBilled {
    transplantDate: string,
    chainId: number,
    clusterNumber: number,
    positionNumber: number,
    donor: {
        id: number,
        alias: string,
        rdnCenterId: number,
        rdnCenterAlias: string,
        center: {
            id: number,
            alias: string
        }
    },
    recipient: {
        id: number,
        alias: string,
        center: {
            id: number,
            alias: string
        }
    },
    claimType: string,
    physicianCharges: number,
    anesthesiaCharges: number,
    hospitalCharges: number
}

export interface IBillingListResponse {
    data: {
        success: boolean,
        results: IBillingDetail[]
    }
}

export interface IBilledByListResponse {
    data: {
        success: boolean,
        results: IBilledCenterForList[]
    }
}

export interface IBilledToListResponse {
    data: {
        success: boolean,
        results: IBilledCenterForList[]
    }
}

export interface ISearchListResponse {
    data: {
        success: boolean,
        results: ISearchForList[]
    }
}

export interface INotBilledListResponse {
    data: {
        success: boolean,
        results: INotBilled[]
    }
}

export interface IBilledManuallyListResponse {
    data: {
        success: boolean,
        results: INotBilled[]
    }
}

export interface IBillingDetailResponse {
    data: {
        success: boolean,
        results: IBillingDetail
    }
}

export interface IApSbsResponse {
    data: {
        success: boolean,
        results: IApSbsItem[]
    }
}

export interface IBillingCenterReportResponse {
    data: {
        success: boolean,
        results: ICenterBillingRecord[]
    }
}

export interface ISaveBillingDetails {
    donorCenterId: number,
    donorId: number,
    centerRoutingNumber: string,
    centerAccountNumber: string,
    centerW9File: File,
    recipientId: number,
    physicianCharges: number,
    physicianChargesFile: File,
    anesthesiaCharges: number,
    anesthesiaChargesFile: File,
    hospitalCharges: number,
    hospitalChargesFile: File,
    asaUnitCount: number,
    isRecipientCenter: boolean,
    accept: boolean
}
export interface IDonorEvalResponse {
    data: {
        success: boolean,
        results: IDonorEvalRecord[]
    }
}

export interface IValidateAchRoutingNumberResponse {
    data: {
        success: boolean,
        message: string,
        results: boolean
    }
}

export const ActionCreators = {
    getSearchList: (centerId: number, status: number, isAccountsReceivable: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<ISearchListResponse> => {
        let url = `${__API__}/billing/${centerId}?status=${status}&isAccountsReceivable=${isAccountsReceivable}`

        let options = {
            url
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: ISearchListResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setSearchList(response.data.results));

            return Promise.resolve(response);
        });
    },
    setSearchList: (list: ISearchForList[]) => {
        return {
            type: ActionTypes.SET_SEARCH_LIST,
            list
        };
    },

    getApSbsReport: (isToDo: boolean = true, startDate: string = null, endDate: string = null) => (dispatch: Dispatch<IApplicationState>): Promise<IApSbsResponse> => {
        let url = `${__API__}/admin/billing/accounts-payable-sbs?isToDo=${isToDo}&start=${startDate}&end=${endDate}`

        let options = {
            url
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IApSbsResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setApSbsReport(response.data.results));

            return Promise.resolve(response);
        });
    },
    setApSbsReport: (list: IApSbsItem[]) => {
        return {
            type: ActionTypes.SET_AP_SBS_REPORT,
            list
        };
    },

    getSBSTransplantFeesResults: () => (dispatch: Dispatch<IApplicationState>) => {
        var options: IGet = {
            url: __API__ + `/admin/billing/accounts-payable-sbs/getsbsresults`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },


    getPendingList: (centerId: number, isRecipientCenter: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IBillingListResponse> => {
        let options = {
            url: `${__API__}/billing/pending?centerId=${centerId}&isRecipientCenter=${isRecipientCenter}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IBillingListResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setPendingList(response.data.results));

            return Promise.resolve(response);
        });
    },

    setPendingList: (list: IBillingDetail[]) => {
        return {
            type: ActionTypes.SET_PENDING_LIST,
            list
        };
    },
    getBilledByList: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBilledByListResponse> => {
        let url = __API__ + '/billing/billed-by-center/' + centerId;

        let options = {
            url
        };

        let response = await dispatch(AuthActionCreators.apiGet(options)) as IBilledByListResponse;

        if (response.data.success) {
            dispatch(ActionCreators.setBilledByList(response.data.results));
        }

        return response;
    },

    getCenterBillingByList: (organType: OrganType, receivableCenterId?: number, payableCenterId?: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBillingCenterReportResponse> => {
        let url = `${__API__}/billing/center-billing?receivableCenterId=${receivableCenterId}&payableCenterId=${payableCenterId}`;

        let options = {
            url
        };

        let response = await dispatch(AuthActionCreators.apiGet(options)) as IBillingCenterReportResponse;

        return response;
    },


    getCenterBillingAccountsReceivableOpoList: (receivableCenterId?: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBillingCenterReportResponse> => {
        let url = __API__ + '/billing/center-billing-opo/' + receivableCenterId;

        let options = {
            url
        };

        let response = await dispatch(AuthActionCreators.apiGet(options)) as IBillingCenterReportResponse;

        return response;
    },

    getCenterBillingAccountsPayableTotalList: (payableCenterId?: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBillingCenterReportResponse> => {
        let url = __API__ + '/billing/center-billing-total-payable/' + payableCenterId;

        let options = {
            url
        };

        let response = await dispatch(AuthActionCreators.apiGet(options)) as IBillingCenterReportResponse;

        return response;
    },




    setBilledByList: (list: IBilledCenterForList[]) => {
        return {
            type: ActionTypes.SET_BILLED_BY_LIST,
            list
        };
    },
    getBilledToList: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBilledByListResponse> => {
        let url = __API__ + '/billing/billed-to-center/' + centerId;

        let options = {
            url
        };

        let response = await dispatch(AuthActionCreators.apiGet(options)) as IBilledToListResponse;

        if (response.data.success) {
            dispatch(ActionCreators.setBilledToList(response.data.results));
        }

        return response;
    },
    setBilledToList: (list: IBilledCenterForList[]) => {
        return {
            type: ActionTypes.SET_BILLED_TO_LIST,
            list
        };
    },
    getNotBilledList: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<INotBilledListResponse> => {
        var options = {
            url: __API__ + '/billing/not-billed/' + centerId
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: INotBilledListResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setNotBilledList(response.data.results));

            return Promise.resolve(response);
        });
    },
    setNotBilledList: (list: INotBilled[]) => {
        return {
            type: ActionTypes.SET_NOT_BILLED_LIST,
            list
        };
    },
    getUpcomingList: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBillingListResponse> => {
        var options = {
            url: `${__API__}/billing/upcoming/${centerId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IBillingListResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setUpcomingList(response.data.results));

            return Promise.resolve(response);
        });
    },
    setUpcomingList: (list: IBillingDetail[]) => {
        return {
            type: ActionTypes.SET_UPCOMING_LIST,
            list
        };
    },
    getSubmittedByRecipientCenter: (centerId: number, isRecipientCenter: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IBillingListResponse> => {
        var options = {
            url: `${__API__}/billing/submitted-by-recipient-center?centerId=${centerId}&isRecipientCenter=${isRecipientCenter}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IBillingListResponse) => {
            return Promise.resolve(response);
        });
    },
    getBilledManuallyList: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<INotBilledListResponse> => {
        var options = {
            url: __API__ + '/billing/billed-manually/' + centerId
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: INotBilledListResponse) => {
            return Promise.resolve(response);
        });
    },
    getDetails: (donorId: number, centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBillingDetailResponse> => {
        let options = {
            url: `${__API__}/billing/detail/${donorId}?centerId=${centerId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IBillingDetailResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setDetails(response.data.results));

            return Promise.resolve(response);
        });
    },
    setDetails: (details: IBillingDetail) => {
        return {
            type: ActionTypes.SET_DETAILS,
            details
        };
    },
    saveDetails: (details: ISaveBillingDetails) => (dispatch: Dispatch<IApplicationState>): Promise<IBillingDetailResponse> => {
        let formData = new FormData();

        formData.append('DonorCenterId', details.donorCenterId.toString());
        formData.append('DonorId', details.donorId.toString());
        formData.append('CenterRoutingNumber', details.centerRoutingNumber);
        formData.append('CenterAccountNumber', details.centerAccountNumber);
        formData.append('RecipientId', details.recipientId.toString());
        formData.append('PhysicianCharges', details.physicianCharges.toString());
        formData.append('AnesthesiaCharges', details.anesthesiaCharges.toString());
        formData.append('HospitalCharges', details.hospitalCharges.toString());
        formData.append('AsaUnitCount', details.asaUnitCount.toString());
        formData.append('isRecipientCenter', details.isRecipientCenter.toString());
        formData.append('accept', details.accept.toString());

        if (details.physicianChargesFile != null) {
            formData.append('PhysicianChargesFile', details.physicianChargesFile);
        }

        if (details.anesthesiaChargesFile != null) {
            formData.append('AnesthesiaChargesFile', details.anesthesiaChargesFile);
        }

        if (details.hospitalChargesFile != null) {
            formData.append('HospitalChargesFile', details.hospitalChargesFile);
        }

        if (details.centerW9File != null) {
            formData.append('CenterW9File', details.centerW9File);
        }

        let options = {
            url: __API__ + '/billing/detail',
            data: formData,
        };

        return dispatch(AuthActionCreators.apiPostFormData(options)).then((response: IBillingDetailResponse) => {
            if (response.data.success) {
                dispatch(ActionCreators.setDetails(response.data.results));
            }

            return Promise.resolve(response);
        });
    },
    saveAch: (donorCenterId: number, donorId: number, centerRoutingNumber: string, centerAccountNumber: string, centerW9File: File) => (dispatch: Dispatch<IApplicationState>): Promise<IBillingDetailResponse> => {
        let formData = new FormData();

        formData.append('DonorCenterId', donorCenterId.toString());
        formData.append('DonorId', donorId.toString());
        formData.append('CenterRoutingNumber', centerRoutingNumber);
        formData.append('CenterAccountNumber', centerAccountNumber);
        formData.append('isRecipientCenter', 'false');

        if (centerW9File != null) {
            formData.append('CenterW9File', centerW9File);
        }

        let options = {
            url: __API__ + '/billing/ach',
            data: formData,
        };

        return dispatch(AuthActionCreators.apiPostFormData(options)).then((response: IBillingDetailResponse) => {
            if (response.data.success) {
                dispatch(ActionCreators.setDetails(response.data.results));
            }

            return Promise.resolve(response);
        });
    },
    saveRecipientInvoiceDetails: (details: IRecipientCenterInvoiceRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IBillingDetailResponse> => {
        let options = {
            url: __API__ + '/billing/recipient-invoice',
            data: details
        }

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBillingDetailResponse) => {
            if (response.data.success) {
                dispatch(ActionCreators.setDetails(response.data.results));
            }

            return Promise.resolve(response);
        });
    },
    updateApSbsItem: (sbsItem: IApSbsItem) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/billing/accounts-payable-sbs`,
            data: {
                donorId: sbsItem.donorId,
                surgeonFee: sbsItem.surgeonFee,
                anesthesiologistFee: sbsItem.anesthesiologistFee,
                hospitalFee: sbsItem.hospitalFee,
                achConfirmation: sbsItem.achConfirmation,
                paidDateTime: sbsItem.paidDateTime,
                comments: sbsItem.comments
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },
    getFile: (donorId: number, docType: string, fileName: string) => (dispatch: Dispatch<IApplicationState>, getState: () => IApplicationState) => {
        let token = getState().auth.authInfo.access_token;
        let getUrl = __API__ + '/billing/document/' + donorId + '/' + docType;

        axios({
            url: getUrl,
            method: 'GET',
            responseType: 'blob',
            headers: {
                'Authorization': 'Bearer ' + token
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        })
    },
    markBilledManually: (donorId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: __API__ + `/billing/billed-manually/${donorId}/${recipientId}`
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    markPaid: (donorId: number, recipientId: number, datePaid: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/billing/paid`,
            data: {
                donorId,
                recipientId,
                datePaid
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    saveMedicareParticipating: (centerId: number, medicareParticipating: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options = {
            url: `${__API__}/center/${centerId}/medicare-participating/${medicareParticipating}`
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IBaseResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.updateMedicareParticpating(medicareParticipating));

            return Promise.resolve(response);
        });
    },
    updateMedicareParticpating: (medicareParticipating: number) => {
        return {
            type: ActionTypes.UPDATE_MEDICARE_PARTICIPATING,
            medicareParticipating
        };
    },

    getDonorEvalReport: (query: DonorEvalQuery) => (dispatch: Dispatch<IApplicationState>): Promise<IDonorEvalResponse> => {
        let url = `${__API__}/admin/billing/accounts-payable-donor-eval?query=${query}`

        let options = {
            url
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IDonorEvalResponse) => {

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    updateDonorEvalRecord: (donorEvalRecord: IDonorEvalRecord) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/billing/accounts-payable-donor-eval`,
            data: {
                donorId: donorEvalRecord.donorId,
                paidDateTime: donorEvalRecord.paidDateTime,
                paidAmount: donorEvalRecord.paymentDueAmount,
                paidAchConfirmation: donorEvalRecord.achConfirmation,
                donorEvalBillType: donorEvalRecord.donorEvalBillType
            }
        };
        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    getDonorEvalResults: (query: DonorEvalQuery) => (dispatch: Dispatch<IApplicationState>) => {
        var options: IGet = {
            url: __API__ + `/admin/billing/accounts-payable-donor-eval/getdonorevalresults?query=${query}`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    validateAchRoutingNumber: (achRoutingNumber: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IValidateAchRoutingNumberResponse> => {
        let config: IGet = {
            url: `${__API__}/billing/validate-ach-routing-number?achRoutingNumber=${achRoutingNumber}`
        };

        return await dispatch(AuthActionCreators.apiGet(config));
    },
};
